<template>
  <div>
    <TopBar :mode="props.mode" @toggle="$emit('toggle')">
      <div class="flex items-center gap-2 xl:hidden">
        <span class="text-[21px] font-semibold custom:text-lg"
          >Keyword Discovery</span
        >
        <div
          class="w-6 text-sm h-6 rounded-full flex items-center justify-center"
          v-tooltip="{
            content: `<div style='max-width: 500px;'><p>
                    What this tool does for you:
                  </p>
                  <p>
                    Step 1 - Fill in the difficulty modifiers to the left
                  </p>
                  <p>
                    Step 2 - Enter in your EMQ or general keyword you want to
                    rank for
                  </p>
                  <p>
                    Step 3 - This tool will find the most lucrative keywords to
                    try to rank for based on your starting keyword
                  </p>
                  <p>
                    Step 4 - Review the Top 20 keyword ROI and difficulty scores. For further analysis, click 'Next Top 20' or choose a bulk option (20, 50, 100)
                  </p>
                  <p>
                    Step 5 - Sort by “Final ROI” to get the easiest keywords
                    that also make the most money for you (or sort by Traffic +
                    Ease, etc.)
                  </p></div>
                  `,
            html: true,
            distance: 10,
          }"
        >
          <CircleHelp :size="20" :stroke-width="1.75" class="mt-1" />
        </div>
      </div>
    </TopBar>
    <div class="p-4">
      <div class="hidden items-center gap-2 mb-4 xl:flex">
        <span class="text-[21px] font-semibold custom:text-lg"
          >Keyword Discovery</span
        >
        <div
          class="w-6 text-sm h-6 rounded-full flex items-center justify-center"
          :class="
            mode === 'dark'
              ? 'bg-white-100 text-black-100'
              : 'bg-black-100 text-white-100'
          "
          v-tooltip="{
            content: `<div style='max-width: 500px;'><p>
                    What this tool does for you:
                  </p>
                  <p>
                    Step 1 - Fill in the difficulty modifiers to the left
                  </p>
                  <p>
                    Step 2 - Enter in your EMQ or general keyword you want to
                    rank for
                  </p>
                  <p>
                    Step 3 - This tool will find the most lucrative keywords to
                    try to rank for based on your starting keyword
                  </p>
                  <p>
                    Step 4 - Review the Top 20 keyword ROI and difficulty scores. For further analysis, click 'Next Top 20' or choose a bulk option (20, 50, 100)
                  </p>
                  <p>
                    Step 5 - Sort by “Final ROI” to get the easiest keywords
                    that also make the most money for you (or sort by Traffic +
                    Ease, etc.)
                  </p></div>
                  `,
            html: true,
            distance: 10,
          }"
        >
          <CircleHelp :size="20" :stroke-width="1.75" class="mt-1" />
        </div>
      </div>
      <span class="mb-2 block font-semibold text-[17px]"
        >Step 1: Enter niche to explore</span
      >
      <div
        class="rounded-xl shadow-card"
        :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
      >
        <nav
          class="border-b flex space-x-4 px-1 xs:flex-col xs:space-x-0"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <label
            for="keywords"
            class="inline-flex text-[15px] items-center relative p-4 nav-tab cursor-pointer"
            :class="[
              selectedSearchForm === 'keywords'
                ? 'active font-medium'
                : mode === 'dark'
                ? 'text-dark-15'
                : 'text-gray-500',
            ]"
          >
            <input
              type="radio"
              value="keywords"
              @change="onChangeTab"
              v-model="selectedSearchForm"
              class="hidden"
              id="keywords"
              :checked="selectedSearchForm === 'keywords'"
            />
            Start with keywords
            <span
              class="w-5 h-5 rounded-full flex items-center justify-center ml-2"
              v-tooltip="{
                content: `For a new website or blog to help find the best keywords to target`,
                distance: 10,
              }"
              ><CircleHelp :size="20" :stroke-width="1.75"
            /></span>
          </label>
          <label
            for="website"
            class="inline-flex text-[15px] items-center relative p-4 nav-tab cursor-pointer"
            :class="[
              selectedSearchForm === 'website'
                ? 'active font-medium'
                : mode === 'dark'
                ? 'text-dark-15'
                : 'text-gray-500',
            ]"
          >
            <input
              type="radio"
              value="website"
              @change="onChangeTab"
              v-model="selectedSearchForm"
              class="hidden"
              id="website"
              :checked="selectedSearchForm === 'website'"
            />
            Start with existing website
            <span
              class="w-5 h-5 rounded-full flex items-center justify-center ml-2"
              v-tooltip="{
                content: `For an existing site to help discover new topics/keywords for which to write content`,
                distance: 10,
              }"
              ><CircleHelp :size="20" :stroke-width="1.75"
            /></span>
          </label>
          <label
            for="keywords-in-bulk"
            class="inline-flex text-[15px] items-center relative p-4 nav-tab cursor-pointer"
            :class="[
              selectedSearchForm === 'keywords-in-bulk'
                ? 'active font-medium'
                : mode === 'dark'
                ? 'text-dark-15'
                : 'text-gray-500',
            ]"
          >
            <input
              type="radio"
              value="keywords-in-bulk"
              @change="onChangeTab"
              v-model="selectedSearchForm"
              class="hidden"
              id="keywords-in-bulk"
              :checked="selectedSearchForm === 'keywords-in-bulk'"
            />
            Import (and analyze) keywords in bulk
            <span
              class="w-5 h-5 rounded-full flex items-center justify-center ml-2"
              v-tooltip="{
                content: `<div style='max-width: 550px'>Start by clicking Import file (CSV Format: 1 column of your keywords). Then enter your Primary Keyword for this list of keywords. The import feature is best for analyzing similar niche related keywords. Last select the location and language.</div>`,
                html: true,
                distance: 10,
              }"
              ><CircleHelp :size="20" :stroke-width="1.75"
            /></span>
          </label>
        </nav>
        <div class="px-4 py-3.5" v-if="selectedSearchForm === 'keywords'">
          <form @submit.prevent="createNewPlan">
            <div class="flex items-center gap-2 xs:flex-col">
              <div class="flex-1 xs:w-full">
                <input
                  ref="keywordDiscoveryInput"
                  type="text"
                  class="form-field"
                  placeholder="Start here - enter in seed / base keyword here"
                  v-model="keyword"
                  required
                />
              </div>
              <div class="flex-1 xs:w-full">
                <Multiselect
                  @open="handleOpenOptionList"
                  v-model="location"
                  class="form-field"
                  placeholder="Search by typing your location"
                  label="full_name"
                  valueProp="full_name"
                  :delay="0"
                  :searchable="true"
                  :options="
                    async function (searchQuery) {
                      if (!searchQuery) return await fetchLocations('a');
                      return await fetchLocations(searchQuery);
                    }
                  "
                  object
                >
                </Multiselect>
              </div>
              <div class="flex-1 xs:w-full">
                <Multiselect
                  v-model="languageSelected"
                  :options="languages"
                  class="form-field"
                  placeholder="Choose language"
                  :searchable="true"
                  :canDeselect="true"
                ></Multiselect>
              </div>
            </div>
            <div class="flex items-center gap-2 mt-2 xs:flex-col">
              <div class="form-field flex items-center">
                <div class="checkbox">
                  <input
                    id="disable-automatic-difficulty-scoring"
                    type="checkbox"
                    v-model="automateDifficulty"
                  />
                  <label for="disable-automatic-difficulty-scoring"
                    >Disable automatic difficulty scoring</label
                  >
                </div>
              </div>
              <div class="flex items-center form-field gap-2">
                <span>Keyword ideas:</span>
                <div class="flex items-center gap-4">
                  <div
                    class="checkbox"
                    v-tooltip="{
                      content: `<div style='max-width: 230px;'>
                        <ul>
                          <li>Casts the widest net</li>
                          <li>Expands reach with AI</li>
                        </ul>
                        </div>`,
                      html: true,
                      distance: 10,
                    }"
                  >
                    <input
                      type="radio"
                      :value="`wide net`"
                      v-model="missingEntities"
                      id="entity-wide-net"
                      :checked="missingEntities === 'wide net'"
                    />
                    <label for="entity-wide-net">Wide net</label>
                  </div>
                  <div
                    class="checkbox"
                    v-tooltip="{
                      content: `<div style='max-width: 230px;'>
                        <ul>
                          <li>Google Keyword Planner ideas only</li>
                          <li>Does not broaden search with our AI</li>
                        </ul>
                        </div>`,
                      html: true,
                      distance: 10,
                    }"
                  >
                    <input
                      type="radio"
                      :value="`focused`"
                      v-model="missingEntities"
                      id="entity-focused"
                      :checked="missingEntities === 'focused'"
                    />
                    <label for="entity-focused">Focused</label>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="px-4 py-3.5" v-if="selectedSearchForm === 'website'">
          <div class="flex items-center gap-2 xs:flex-col">
            <div class="flex-1 xs:w-full">
              <input
                type="text"
                class="form-field"
                placeholder="Website (e.g. domain.com or domain.com/page)"
                v-model="website"
              />
            </div>
            <div class="flex-1 xs:w-full">
              <Multiselect
                @open="handleOpenOptionList"
                v-model="location"
                class="form-field"
                placeholder="Search by typing your location"
                label="full_name"
                valueProp="full_name"
                :delay="0"
                :searchable="true"
                :options="
                  async function (searchQuery) {
                    if (!searchQuery) return await fetchLocations('a');
                    return await fetchLocations(searchQuery);
                  }
                "
                object
              >
              </Multiselect>
            </div>
            <div class="flex-1 xs:w-full">
              <Multiselect
                v-model="languageSelected"
                :options="languages"
                class="form-field"
                placeholder="Choose language"
                :searchable="true"
                :canDeselect="true"
              ></Multiselect>
            </div>
          </div>
          <div class="flex items-center gap-2 mt-2 xs:flex-col">
            <div class="form-field flex items-center">
              <div class="checkbox">
                <input
                  id="disable-automatic-difficulty-scoring"
                  type="checkbox"
                  v-model="automateDifficulty"
                />
                <label for="disable-automatic-difficulty-scoring"
                  >Disable automatic difficulty scoring
                </label>
              </div>
            </div>
            <div class="flex items-center form-field gap-2">
              <span>Keyword ideas:</span>
              <div class="flex items-center gap-4">
                <div
                  class="checkbox"
                  v-tooltip="{
                    content: `<div style='max-width: 230px;'>
                        <ul>
                          <li>Casts the widest net</li>
                          <li>Expands reach with AI</li>
                        </ul>
                        </div>`,
                    html: true,
                    distance: 10,
                  }"
                >
                  <input
                    type="radio"
                    :value="`wide net`"
                    v-model="missingEntities"
                    id="entity-wide-net"
                    :checked="missingEntities === 'wide net'"
                  />
                  <label for="entity-wide-net"> Wide net</label>
                </div>
                <div
                  class="checkbox"
                  v-tooltip="{
                    content: `<div style='max-width: 230px;'>
                        <ul>
                          <li>Google Keyword Planner ideas only</li>
                          <li>Does not broaden search with our AI</li>
                        </ul>
                        </div>`,
                    html: true,
                    distance: 10,
                  }"
                >
                  <input
                    type="radio"
                    :value="`focused`"
                    v-model="missingEntities"
                    id="entity-focused"
                    :checked="missingEntities === 'focused'"
                  />
                  <label for="entity-focused"> Focused </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="px-4 py-3.5"
          v-if="selectedSearchForm === 'keywords-in-bulk'"
        >
          <form @submit.prevent="createNewPlanList">
            <div class="flex items-end gap-2 xs:flex-col">
              <div class="flex-1 xs:w-full">
                <input
                  type="file"
                  id="csvFileInput"
                  accept=".csv"
                  class="hidden"
                  @change="handleFileSelect"
                />
                <div class="flex items-center mb-1 gap-1" v-if="csvFile">
                  <div class="text-[15px]">
                    Import success: {{ csvFile?.name }}
                  </div>
                  <div
                    @click="removeFile"
                    class="hover:text-danger-100 cursor-pointer"
                  >
                    <Trash2 :size="18" :stroke-width="1.75" />
                  </div>
                </div>
                <label for="csvFileInput" class="button primary-button w-full">
                  <Download :size="20" :stroke-width="1.75" />
                  Import File
                </label>
              </div>
              <div class="flex-1 xs:w-full">
                <input
                  type="text"
                  class="form-field"
                  placeholder="Enter the seed keyword for this list"
                  v-model="seedKeyword"
                />
              </div>
              <div class="flex-1 xs:w-full">
                <Multiselect
                  @open="handleOpenOptionList"
                  v-model="location"
                  class="form-field"
                  placeholder="Search by typing your location"
                  label="full_name"
                  valueProp="full_name"
                  :delay="0"
                  :searchable="true"
                  :options="
                    async function (searchQuery) {
                      if (!searchQuery) return await fetchLocations('a');
                      return await fetchLocations(searchQuery);
                    }
                  "
                  object
                >
                </Multiselect>
              </div>
              <div class="flex-1 xs:w-full">
                <Multiselect
                  v-model="languageSelected"
                  :options="languages"
                  class="form-field"
                  placeholder="Choose language"
                  :searchable="true"
                  :canDeselect="true"
                ></Multiselect>
              </div>
            </div>
          </form>
        </div>
      </div>
      <div class="my-4">
        <span class="mb-2 block font-semibold text-[17px]"
          >Step 2: Select ranking difficulty modifiers</span
        >
        <div
          class="rounded-xl shadow-card"
          :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
        >
          <div class="flex items-center justify-between gap-2 p-4 xs:flex-col">
            <div class="flex items-center gap-2 flex-1 xs:flex-col xs:w-full">
              <template v-for="(question, key) in questions" :key="key">
                <div
                  v-if="!question.includes('Do you plan on using AI content?')"
                  class="form-field flex items-center w-auto xs:w-full"
                  :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
                >
                  <div class="checkbox">
                    <input
                      :id="key"
                      type="checkbox"
                      :name="key"
                      @change="changeAnswer"
                      :checked="answers ? answers[key] === 1 : false"
                    />
                    <label :for="key">{{ question }}</label>
                  </div>
                </div>
              </template>
            </div>
            <div
              v-if="selectedSearchForm === 'keywords-in-bulk'"
              class="xs:w-full"
            >
              <button
                class="button primary-button xs:w-full"
                @click="createNewPlanList"
              >
                <TextSearch :size="20" :stroke-width="1.75" />
                Get results
              </button>
            </div>
            <div v-if="selectedSearchForm === 'website'" class="xs:w-full">
              <button
                class="button primary-button xs:w-full"
                @click="createNewPlanWebsite"
              >
                <TextSearch :size="20" :stroke-width="1.75" />
                Get results
              </button>
            </div>
            <div v-if="selectedSearchForm === 'keywords'" class="xs:w-full">
              <button
                class="button primary-button xs:w-full"
                @click="createNewPlan"
              >
                <TextSearch :size="20" :stroke-width="1.75" />
                Get results
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        class="rounded-xl shadow-card"
        :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
      >
        <div
          class="px-4 py-3.5 border-b flex items-center justify-between xs:flex-col xs:gap-2"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <div class="xs:w-full">
            <h3 class="font-semibold leading-none mb-2 text-[17px]">
              Keyword discovery results
            </h3>
            <p
              class="text-[15px] leading-none"
              :class="mode === 'dark' ? 'text-dark-15' : 'text-gray-600'"
            >
              Total {{ totalResults }}
              {{ totalResults > 1 ? "results" : "result" }} found
            </p>
          </div>
          <div class="flex items-center gap-2 xs:flex-col xs:w-full">
            <div class="flex items-center gap-2 xs:w-full">
              <button
                class="button primary-button"
                :class="{
                  'opacity-50 pointer-events-none': filterKDReports === '',
                }"
                @click="switchType('')"
              >
                All
              </button>
              <button
                class="button primary-button"
                :class="{
                  'opacity-50 pointer-events-none':
                    filterKDReports === 'keywords',
                }"
                @click="switchType('keywords')"
              >
                <LayoutList :size="20" :stroke-width="1.75" />
                <span>Keywords</span>
              </button>
              <button
                class="button primary-button"
                :class="{
                  'opacity-50 pointer-events-none':
                    filterKDReports === 'websites',
                }"
                @click="switchType('websites')"
              >
                <Earth :size="20" :stroke-width="1.75" />
                <span>Websites</span>
              </button>
            </div>
            <div class="w-[400px] xs:w-full relative flex items-center">
              <span
                class="absolute bottom-0 left-2.5 flex h-11 items-center text-gray-0 text-opacity-70"
              >
                <Search :size="20" :stroke-width="1.75" />
              </span>
              <input
                type="search"
                class="form-field pl-9"
                :placeholder="`Search by ${filterByKDReport.toLowerCase()}`"
                @input="getSearchResults"
                v-model="dataSearchQuery"
              />
            </div>
          </div>
        </div>
        <div
          class="w-full overflow-x-auto without-scrollbar max-h-screen overflow-y-auto"
        >
          <table class="w-full form-table">
            <thead
              class="border-b-2 sticky top-0 z-10"
              :class="mode === 'dark' ? 'border-dark-50' : ''"
            >
              <tr :class="mode === 'dark' ? 'bg-dark-1' : 'bg-gray-1'">
                <th @click="sortDataBy" class="capitalize">
                  <span class="flex items-center gap-1.5">
                    <DTSort
                      :title="filterByKDReport"
                      :mode="props.mode"
                    ></DTSort>
                  </span>
                </th>
                <th @click="sortData('status')">
                  <span
                    class="inline-block"
                    v-tooltip="`Status will refresh every 5 seconds`"
                  >
                    <span class="flex items-center gap-1.5">
                      <DTSort title="Status" :mode="props.mode"></DTSort>
                    </span>
                  </span>
                </th>
                <th @click="sortData('total_keywords_ideas')">
                  <span class="flex items-center gap-1.5">
                    <DTSort title="Keyword Ideas" :mode="props.mode"></DTSort>
                  </span>
                </th>
                <th @click="sortData('percentage_diff_hard_score_value')">
                  <span
                    class="inline-block"
                    v-tooltip="{
                      content: `<div style='width: 400px'>
                      <span class='fw-bold'>
                        Average keyword difficulty for all keywords in this dataset offers insight into the ranking difficulty of this niche.
                      </div>`,
                      html: true,
                    }"
                  >
                    <span class="flex items-center gap-1.5">
                      <DTSort title="Avg KD" :mode="props.mode"></DTSort>
                    </span>
                  </span>
                </th>
                <th @click="sortData('max_keyword_revenue_ballpark')">
                  <span
                    class="inline-block"
                    v-tooltip="{
                      content: `<div style='width: 400px'>
                      <span class='fw-bold'>Highest Keyword Revenue Ballpark (KRB) in Dataset:</span> Displays the highest estimated revenue for a keyword in your dataset, based on CPC, a 6:1 ROI, and YOY trends. A quick guide to the peak potential in your report.
                    </div>`,
                      html: true,
                    }"
                  >
                    <span class="flex items-center gap-1.5">
                      <DTSort title="Highest KRB" :mode="props.mode"></DTSort>
                    </span>
                  </span>
                </th>
                <th @click="sortData('avg_keyword_revenue_ballpark')">
                  <span
                    class="inline-block"
                    v-tooltip="{
                      content: `<div style='width: 400px'>
                      <span class='fw-bold'>Top 10 Average Keyword Revenue Ballpark (KRB) in Dataset:</span> Shows the average estimated revenue of the top 10 keywords in your dataset, using the same calculation as the highest KRB. Offers insight into the overall performance among top keywords.
                    </div>`,
                      html: true,
                    }"
                  >
                    <span class="flex items-center gap-1.5">
                      <DTSort title="Avg KRB" :mode="props.mode"></DTSort>
                    </span>
                  </span>
                </th>
                <th @click="sortData('country_name')">
                  <span class="flex items-center gap-1.5">
                    <DTSort title="Country" :mode="props.mode"></DTSort>
                  </span>
                </th>
                <th @click="sortData('language')">
                  <span class="flex items-center gap-1.5">
                    <DTSort title="Language" :mode="props.mode"></DTSort>
                  </span>
                </th>
                <th @click="sortData('created_at')">
                  <span class="flex items-center gap-1.5">
                    <DTSort title="Created At" :mode="props.mode"></DTSort>
                  </span>
                </th>
                <th @click="sortData('created_at')">Expiry date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody
              class="divide-y bg-opacity-40"
              :class="
                mode === 'dark'
                  ? 'bg-dark-70 divide-dark-50'
                  : 'bg-white-100 divide-gray-100'
              "
            >
              <template v-for="(plan, key) in filteredPlans" :key="key">
                <tr>
                  <td>
                    <div class="flex items-center">
                      <RouterLink
                        :to="{
                          name: 'KeywordPlannerDetail',
                          params: { uuid: plan.planner_uuid },
                        }"
                        class="font-medium hover:text-primary-100 inline-block text-wrap max-w-[240px]"
                      >
                        {{ plan?.website_url || plan?.text }}
                      </RouterLink>
                    </div>
                  </td>
                  <td>
                    <span
                      v-if="plan.status === 10"
                      class="inline-flex items-center text-center h-7 px-2 text-[13px] font-medium rounded-md capitalize text-primary-100 bg-primary-100 bg-opacity-10"
                      >{{ statuses[plan.status] }}</span
                    >
                    <span
                      v-if="plan.status === 11"
                      class="inline-flex items-center text-center h-7 px-2 text-[13px] font-medium rounded-md capitalize text-info-100 bg-info-100 bg-opacity-10"
                      >{{ statuses[plan.status] }}</span
                    >
                    <span
                      v-if="plan.status === 12"
                      class="inline-flex items-center text-center h-7 px-2 text-[13px] font-medium rounded-md capitalize text-warning-100 bg-warning-100 bg-opacity-10"
                      >{{ statuses[plan.status] }}</span
                    >
                    <span
                      v-if="plan.status === 13"
                      class="inline-flex items-center text-center h-7 px-2 text-[13px] font-medium rounded-md capitalize text-info-100 bg-info-100 bg-opacity-10"
                      >{{ statuses[plan.status] }}</span
                    >
                    <span
                      v-if="plan.status === 20"
                      class="inline-flex items-center text-center h-7 px-2 text-[13px] font-medium rounded-md capitalize text-success-100 bg-success-100 bg-opacity-10"
                      >{{ statuses[plan.status] }}</span
                    >
                    <span
                      v-if="plan.status === 30"
                      class="inline-flex items-center text-center h-7 px-2 text-[13px] font-medium rounded-md capitalize text-danger-100 bg-danger-100 bg-opacity-10"
                      >{{ statuses[plan.status] }}</span
                    >
                  </td>
                  <td>{{ plan?.total_keywords_ideas || "-" }}</td>
                  <td>
                    <span
                      v-if="plan.percentage_diff_hard_score_value"
                      v-tooltip="{
                        content:
                          plan.percentage_diff_hard_score_value >= 0
                            ? `+${plan.percentage_diff_hard_score_value}%`
                            : `${plan.percentage_diff_hard_score_value}%`,
                        distance: 10,
                      }"
                      class="inline-flex items-center text-center h-7 px-2 text-[13px] font-medium rounded-md capitalize"
                      :class="
                        plan.percentage_diff_hard_score_text
                          .toLowerCase()
                          .split(' ')
                          .join('-')
                      "
                    >
                      {{ plan.percentage_diff_hard_score_text }}
                    </span>
                    <span
                      class="inline-block"
                      v-else
                      v-tooltip="{
                        content: `No keyword difficulty scores available yet.`,
                        distance: 10,
                      }"
                    >
                      -
                    </span>
                  </td>
                  <td>
                    {{
                      (plan?.max_keyword_revenue_ballpark &&
                        addCommas(plan.max_keyword_revenue_ballpark)) ||
                      "-"
                    }}
                  </td>
                  <td>
                    {{
                      (plan?.avg_keyword_revenue_ballpark &&
                        addCommas(plan.avg_keyword_revenue_ballpark)) ||
                      "-"
                    }}
                  </td>
                  <td>
                    <span class="text-wrap">{{
                      plan?.country_name || "All Locations (Global)"
                    }}</span>
                  </td>
                  <td>
                    {{
                      plan && plan.language ? plan.language.toLowerCase() : "-"
                    }}
                  </td>
                  <td>{{ formatDate(plan.created_at) }}</td>
                  <td>
                    <RouterLink
                      :to="{
                        name: 'account-subscription',
                      }"
                      v-tooltip="{
                        content: 'Click to upgrade your plan',
                      }"
                      :class="[
                        isNearExpiry(plan.expiration_date)
                          ? 'text-danger-100'
                          : '',
                      ]"
                      v-if="plan.expiration_date"
                    >
                      {{ formatDate(plan.expiration_date) }}
                    </RouterLink>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <div
                      @click="deleteKDReport(plan)"
                      class="w-5 h-5 cursor-pointer hover:text-danger-100"
                      title="Delete"
                    >
                      <Trash2 :size="18" :stroke-width="1.75" />
                    </div>
                  </td>
                </tr>
              </template>
              <tr v-if="!filteredPlans.length" class="!bg-transparent">
                <td colspan="11">
                  <EmptyState
                    :mode="mode"
                    :notFound="`No ${
                      dataSearchQuery ? 'results' : 'data'
                    } found!`"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          class="border-t-4 rounded-bl-xl rounded-br-xl"
          :class="
            mode === 'dark' ? 'border-dark-50 bg-dark-70' : 'bg-white-100'
          "
        >
          <div class="flex items-center px-4 py-3 justify-between">
            <nav class="flex items-center gap-2">
              <button
                :disabled="!previousPageUrl"
                class="button primary-button"
                @click="listPlanner('previous')"
              >
                <ArrowLeft :size="20" :stroke-width="1.75" class="mt-[2px]" />
                Previous
              </button>
              <button
                :disabled="!nextPageUrl"
                class="button primary-button"
                @click="listPlanner('next')"
              >
                Next
                <ArrowRight :size="20" :stroke-width="1.75" class="mt-[2px]" />
              </button>
            </nav>
            <div class="w-40" v-if="!store.isloading">
              <Multiselect
                @change="handleChangeReportPerPage"
                :value="selectedReportsPerPage"
                :options="reportsPerPage"
                :canClear="false"
                :searchable="false"
                :canDeselect="false"
                placeholder="Reports per page"
                class="form-field to-top"
              ></Multiselect>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted } from "vue";
import type { Ref } from "vue";
import type {
  KDList,
  KPlan,
  KDQuestions,
  KDQuestionAnswers,
  CreatePlanPayload,
} from "../libs/types";
import { DEFAULT_MODE } from "../libs/constants";
import { debounce } from "lodash";
import { formatDate } from "../libs";
import { useToast } from "vue-toast-notification";
import helper from "../libs/helper";
import TopBar from "../components/TopBar.vue";
import EmptyState from "../components/common/Empty.vue";
import DTSort from "../components/DataTableSortComponent.vue";
import Multiselect from "@vueform/multiselect";
import {
  getPlannerData,
  getPlanners,
  postCreateKeywordPlan,
  deletePlanner,
} from "../libs/Api";
import ApiRequest from "../libs/ApiRequest";
import { useUserStore } from "../stores/user";
import { useRouter } from "vue-router";
import {
  ArrowLeft,
  ArrowRight,
  CircleHelp,
  Download,
  Earth,
  LayoutList,
  Search,
  TextSearch,
  Trash2,
} from "lucide-vue-next";
interface Props {
  mode?: string;
}
const router = useRouter();
const store = useUserStore();
const props = withDefaults(defineProps<Props>(), { mode: DEFAULT_MODE });
const KDPlanners: Ref<KPlan[]> = ref([]);
const keyword: Ref<string> = ref("");
const website: Ref<string> = ref("");
const languages = ref([]);
const languageSelected = ref(null);
const questions: Ref<KDQuestions | null> = ref(null);
const answers: Ref<KDQuestionAnswers | null> = ref(null);
const interval = ref(null);
const hasNextPage = ref(false);
const currentPage = ref(1);
const totalResults = ref(0);
const location = ref({ full_name: "All Locations (Global)" });
const dataSearchQuery = ref(null);
const seedKeyword: Ref<string> = ref("");
const keywordsList: Ref<string[] | null> = ref([]);
const csvFile = ref(null);
const automateDifficulty = ref(false);
const selectedReportsPerPage = ref(100);
const reportsPerPage = ref([
  { label: 100, value: 100 },
  { label: 200, value: 200 },
  { label: 300, value: 300 },
  { label: 400, value: 400 },
  { label: 500, value: 500 },
]);
const nextPageUrl: Ref<string | null> = ref(null);
const filterKDReports = ref("");
const previousPageUrl: Ref<string | null> = ref(null);
const statuses = ref({
  10: "Pending",
  11: "Fetching keyword planner data",
  12: "AI Generated Keyword Ideas",
  13: "Fetching more keyword idea data",
  20: "Success",
  30: "Failed",
});
const sortDirection = ref("desc");
const sortBy = ref("created_at");
const selectedSearchForm = ref("keywords");
const missingEntities = ref("wide net");
const keywordDiscoveryInput = ref(null);
const filterByKDReport = computed(() => {
  return filterKDReports.value === ""
    ? "Keywords/Websites"
    : filterKDReports.value;
});
const filteredPlans = ref([]);
const $toast = useToast();
onMounted(() => {
  loadPlannerData();
  listPlanner();
  interval.value = setInterval(() => {
    listPlanner("current", "", false);
  }, 5000);
  answers.value = {
    topic: 0,
    content: 0,
    site: 0,
  };
  keywordDiscoveryInput.value.focus();
});
onUnmounted(() => {
  interval.value && clearInterval(interval.value);
});
const handleChangeReportPerPage = (count) => {
  selectedReportsPerPage.value = count;
  listPlanner();
};
const addCommas = (num) => {
  return `$${Number(num.toFixed(0)).toLocaleString()}`;
};
const onChangeTab = () => {
  automateDifficulty.value = false;
  missingEntities.value = "wide net";
};
const removeFile = () => {
  csvFile.value = null;
  keywordsList.value = [];
};
const deleteKDReport = async (plan) => {
  try {
    store.setIsLoading(true);
    const res = await deletePlanner(plan.planner_uuid);
    if (res) {
      await listPlanner();
      $toast.success("Successfully deleted Keyword Discovery planner!");
    }
  } catch (error) {
    const extractedError = helper.extractAlert(error);
    $toast.error(extractedError.alert.message);
  } finally {
    store.setIsLoading(false);
  }
};
const handleFileSelect = (event) => {
  try {
    const file = event.target.files[0];
    csvFile.value = file;
    if (!file) {
      throw new Error("Please select a file!");
    }
    const reader = new FileReader();
    reader.onload = function (event) {
      const contents = event.target.result;
      const lines = contents.split("\n");
      const keywordsFromCSV = [];
      for (let i = 0; i < lines.length; i++) {
        const columns = lines[i].split(",");
        if (columns.length > 0 && columns[0] && columns[0] != "") {
          keywordsFromCSV.push(columns[0]);
        }
      }
      keywordsList.value = keywordsFromCSV;
    };
    reader.readAsText(file);
  } catch (error) {
    const extractedError = helper.extractAlert(error, "keyword-planner");
    $toast.error(extractedError.alert.message);
  }
};
const getSearchResults = debounce((e) => {
  listPlanner("", e.target.value);
}, 400);
const switchType = (type) => {
  dataSearchQuery.value = null;
  filterKDReports.value = type;
  listPlanner();
};
const handleOpenOptionList = () => {
  location.value = null;
};
const fetchLocations = async (searchQuery) => {
  try {
    const { data } = await ApiRequest().get(
      `https://api.valueserp.com/locations?api_key=demo&q=${searchQuery}`
    );
    const obj = {
      full_name: "All Locations (Global)",
    };
    let locations = data && data.locations ? [obj, ...data.locations] : [];
    if (locations.length) {
      locations = locations.map((l) => {
        return { ...l, full_name: l.full_name.split(",").join(", ") };
      });
    }
    return locations;
  } catch (err) {
    console.error("get locations error", err);
  }
};
const sortDataBy = (): void => {
  const sort = {
    all: "planner_name",
    keywords: "text",
    websites: "website_url",
  };
  const by =
    sort[filterKDReports.value === "" ? "all" : filterByKDReport.value];
  if (sortBy.value === by) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  }
  sortBy.value = by;
  applySortedData();
};
const sortData = (by: string): void => {
  if (sortBy.value === by) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  }
  sortBy.value = by;
  applySortedData();
};
const applySortedData = () => {
  if (KDPlanners.value) {
    filteredPlans.value = helper.sortEntities(
      KDPlanners.value,
      sortBy.value,
      sortDirection.value
    );
  } else {
    filteredPlans.value = [];
  }
};
const loadPlannerData = async () => {
  try {
    const detail = await getPlannerData();
    detail.languages.forEach((language) => {
      languages.value.push({ label: language[1], value: language[0] });
    });
    if (languages.value) {
      const defaultLang = languages.value.find(
        (l) => l && l.label && l.label.toLowerCase() === "english"
      );
      languageSelected.value = (defaultLang && defaultLang.value) || null;
    }
    questions.value = detail.questions;
  } catch (error) {
    const extractedError = helper.extractAlert(error);
    $toast.error(extractedError.alert.message);
  }
};
const listPlanner = async (page, filter?: any, isLoading = true) => {
  try {
    if (isLoading) {
      store.setIsLoading(true);
    }
    const data: KDList = await getPlanners(
      selectedReportsPerPage.value,
      page,
      nextPageUrl.value,
      previousPageUrl.value,
      filterKDReports.value,
      filter
    );
    hasNextPage.value = Boolean(data.next);
    currentPage.value = page;
    totalResults.value = data.count || 0;
    nextPageUrl.value = data.next;
    previousPageUrl.value = data.previous;
    KDPlanners.value = data.results;
    if (KDPlanners.value && KDPlanners.value.length) {
      KDPlanners.value = KDPlanners.value.map((planner) => {
        return {
          ...planner,
          planner_name: planner.website_url
            ? planner.website_url
            : planner.text,
        };
      });
    }
    filteredPlans.value = KDPlanners.value;
    if (KDPlanners.value.every((keyword) => keyword.status === 20)) {
      clearInterval(interval.value);
    } else {
      clearInterval(interval.value);
      interval.value = setInterval(() => {
        listPlanner("current", "", false);
      }, 5000);
    }
  } catch (error) {
    const extractedError = helper.extractAlert(error);
    $toast.error(extractedError.alert.message);
  } finally {
    if (isLoading) {
      store.setIsLoading(false);
    }
  }
};
const createNewPlanWebsite = async () => {
  website.value = website.value.replace("https://", "");
  const urlCheckerRegex = new RegExp(
    "^([0-9A-Za-z-\\.@:%_\+~#=]+)+((\\.[a-zA-Z]{2,3})+)(/(.)*)?(\\?(.)*)?"
  );
  if (website.value.length < 1 || !urlCheckerRegex.test(website.value)) {
    return $toast.error("Please enter valid website URL");
  }
  await sendCreatePlanRequest(website, "website");
};
const createNewPlan = async () => {
  if (keyword.value.length < 1) {
    return $toast.error("Please enter valid keyword");
  }
  await sendCreatePlanRequest(keyword, "keyword");
};
const createNewPlanList = async () => {
  if (seedKeyword.value.length < 1) {
    return $toast.error("Please enter the seed keyword for the list");
  }
  if (keywordsList.value.length < 1) {
    return $toast.error("Please select a file for keywords");
  }
  if (keywordsList.value.length > 100) {
    return $toast.error(
      `Allow Maximum 100 keywords, It have ${keywordsList.value.length} keywords`
    );
  }
  await sendCreatePlanRequest(keywordsList, "list");
};
const sendCreatePlanRequest = async (
  keyword: Ref,
  type: "website" | "keyword" | "list"
) => {
  store.setIsLoading(true);
  try {
    if (!location.value) {
      throw new Error("Select/Enter a Location to proceed");
    }
    let payload: CreatePlanPayload = {
      questions: answers.value as KDQuestionAnswers,
    };
    if (type === "website") {
      payload.website_url = keyword.value;
    } else if (type === "list") {
      payload.is_user_input = true;
      payload.text = seedKeyword.value;
      payload.user_keywrod_list = keyword.value;
    } else {
      payload.text = keyword.value;
    }
    if (languageSelected.value) {
      payload.language = languageSelected.value;
    }
    payload = {
      ...payload,
      ...(location.value && location.value.id
        ? {
            country: location.value.id.toString(),
          }
        : {}),
      ...(location.value &&
      location.value.full_name === "All Locations (Global)"
        ? {}
        : { country_name: location.value.full_name }),
      automate_difficulty: automateDifficulty.value === true ? false : true,
      missing_entities: missingEntities.value === "wide net" ? true : false,
    };
    const { data, status } = await postCreateKeywordPlan(payload);
    if (!data.planner_uuid || status !== 201) {
      throw new Error("Something went wrong!");
    }
    keyword.value = "";
    website.value = "";
    listPlanner("", "");
  } catch (error) {
    const extractedError = helper.extractAlert(error);
    $toast.error(extractedError.alert.message);
    if (
      extractedError.alert.message ===
      "You've exhausted your monthly quota. Please upgrade for more credits"
    ) {
      router.push({
        name: "account-subscription",
      });
    }
  } finally {
    store.setIsLoading(false);
  }
};
const changeAnswer = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (answers.value && typeof answers.value === "object") {
    answers.value[target.name] = target.checked ? 1 : 0;
  }
};
const isNearExpiry = (date) => {
  const now = new Date();
  const expiryDate = new Date(date);
  const timeDiff = expiryDate - now;
  const daysLeft = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  return daysLeft < 3;
};
</script>
<style src="@vueform/multiselect/themes/default.scss"></style>
